<template>
  <section class="input-wrapper">
    <div
      v-if="showLabel"
      :class="[
        'flex justify-between items-center py-2 text-sm text-[#344054] font-medium',
        classString,
      ]"
    >
      <label :for="id"
      >{{ $t(`${label}`) }}
        <span
          v-if="showLabelBadge"
          class="bg-red-100 text-red-500 text-xs px-2 py-1 rounded-lg"
        >
          {{ labelBadgeText }}
        </span>
      </label>

      <div class="ml-auto">
        <slot name="otherInputFields"></slot>
      </div>
    </div>
    <div
      :class="[
        'input relative w-full',
        { 'pl-4': inputType === 'website', focused, '!border-0': noBorders },
      ]"
    >
      <div
        v-if="inputType === 'website'"
        class="border-r-2 h-[48px] pr-3 flex items-center justify-center"
      >
        <span class="text-[#667085]">https://</span>
      </div>
      <slot name="inputIcon"></slot>
      <input
        :disabled="disabled"
        :type="
          inputType === 'password'
            ? 'password'
            : inputType === 'date'
            ? 'date'
            : 'text'
        "
        :name="id"
        :id="id"
        :class="[
          {
            'border !border-red-500 rounded-lg': error,
            'cursor-pointer': type === 'date',
            'text-right': currency,
          },
          'placeholder:text-sm w-full',
        ]"
        :style="[inputStyling, { height, padding }]"
        :placeholder="placeholder"
        :value="inputValue"
        :validation="validation"
        :min="type === 'date' ? getMinDate() : null"
        :max="type === 'date' ? getMaxDate() : null"
        @input="updateValue"
        @focus="focused = true"
        @blur="handleOnBlur"
        @keydown="$emit('keyDown', $event)"
        :readonly="readonly"
      />
      <div
        aria-describedby="button"
        :class="[
          'absolute right-0 flex mr-2 transform -translate-y-1/2 item-center top-1/2',
        ]"
      >
        <span aria-describedby="button" v-if="type === 'password' || error">
          <v-icon
            size="20"
            :icon="error ? 'custom:errorIcon' : filterIcon"
            color="#98A2B3"
            @click="showPassword"
          />
        </span>
        <span v-else>
          <slot name="inputActionIcon">
            <v-icon
              class="bg-gray-100 rounded-lg hover:bg-gray-200"
              v-if="showAddIcon"
              size="20"
              :icon="error ? 'custom:errorIcon' : 'mdi-plus'"
              color="#98A2B3"
              @click="$emit('add', modelValue)"
            />
          </slot>
        </span>
      </div>
    </div>
    <p v-if="error" class="text-red-500 text-[14px]">
      {{ $t(`${errorMessage.getTranslationKey()}`) }}
    </p>
  </section>
</template>

<script lang="js">
import { germanToNumberIntl, numberToGerman, restrictCharactersByRegex } from "@/services/helpers";
import { defineComponent } from "vue";

export default defineComponent({
  name: "InputField",
  props: {
    id: {
      type: String,
      default: "input-field",
    },
    label: {
      type: String,
      default: "",
    },
    modelValue: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "text",
    },
    placeholder: {
      type: String,
      default: "",
    },
    errorMessage: {
      type: String,
      default: "This field is required",
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    validation: {
      type: String,
      default: "",
    },
    classString: {
      type: String,
      default: "",
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    inputStyling: {},
    height: {
      type: String,
      default: "44px"
    },
    padding: {
      type: String,
      default: "10px 14px"
    },
    currency: {
      type: Boolean,
      default: false
    },
    showAddIcon: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    showLabelBadge: {
      type: Boolean,
      default: false,
    },
    labelBadgeText: {
      type: String,
      default: "Disapproved",
    },
    noBorders: {
      type: Boolean,
      default: false
    }

  },
  data() {
    return {
      error: false,
      inputType: this.type,
      // errorMessages: this.errorMessage,
      regexValidation: {
        // will remove this soon
        lettersOnly: /[^a-zA-Z\s]/g,
        lettersOnlyAndSpace: /[^a-zA-Z\s]/g,
        lettersSpecialCharactersOnly: /[^ÃåÅÄäÀàÁáÂâÇçČčÉéÈèÊêËëĔĕĞğĢģÏïÎîÍíÌìÑñÖöÔôŌōÒòÓóØøŜŝŞşÜüŪūÛûÙùÚúŸÿßẞÞþÐða-zA-Z_-]/g,
        phoneOnly: /[^0-9+]/g,
        digitOnly: /[^0-9,.]/g,  // Only allow digits and comma (German decimal separator)
        digitsAndLettersOnly: /[^a-zA-Z0-9]/g,
        withoutWhitespace: /^\S.*$/g,
        floatingNumber: /[^0-9,.-]/g,  // Only allow digits, comma, and minus (German format prioritized)
      },
      focused: false,
      inputValue: "",
      modelUpdateTimeout: null,
      isUserTyping: false
    };
  },
  watch: {
    hasError(val) {
      this.error = val
    },
    modelValue: {
      handler(val){
        // Don't update inputValue if user is actively typing
        if(this.isUserTyping) {
          return;
        }

        if(val && (this.validation === "digitOnly" || this.validation === "floatingNumber" || this.type === "number")) {
          const numericValue = parseFloat(val);
          if (!isNaN(numericValue)) {
            this.inputValue = numberToGerman(numericValue)
          }
        } else {
          this.inputValue = val
        }
        this.error = this.isRequired && this.hasError
      },
      immediate: true
    }
  },
  computed: {
    filterIcon() {
      return this.inputType === 'password' ? "mdi-eye-outline" : "mdi-eye-off-outline";
    }
  },

  methods: {
    showPassword(){
      if (this.type === 'password') {
        this.inputType = this.inputType === 'password' ? 'text' : 'password';
      }
    },
    getMinDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 200);
      return date.toISOString().slice(0, 10);
    },
    getMaxDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() + 100);
      return date.toISOString().slice(0, 10);
    },

    handleOnBlur(event) {
      this.$emit('blur', event)
      this.focused = false;
      this.isUserTyping = false; // Reset typing flag when user leaves the field
    },

    updateValue(event) {
      // Set typing flag to prevent watcher from interfering
      this.isUserTyping = true;

      // Apply regex restriction if validation is specified
      if(this.validation && this.regexValidation[this.validation]) {
        restrictCharactersByRegex(event, this.regexValidation[this.validation]);
      }

      const value = event.target.value;

      // Update the displayed value to match what user typed
      this.inputValue = value;

      if(this.validation === "digitOnly" || this.validation === "floatingNumber" || this.type === "number") {

        if(value.includes(',') || value.includes('.')) {
          // Contains separator(s) - treat as German format
          const standardFormat = germanToNumberIntl(value)
          // Emit the standard format as modelValue
          this.$emit('update:modelValue', standardFormat?.toString() || '')
        } else {
          // No separators, emit as-is (plain whole number like "277500")
          this.$emit('update:modelValue', value)
        }
      } else {
        // For non-numeric validations, emit as-is
        this.$emit('update:modelValue', value)
      }

      // Update error state
      if (value.length > 0) {
        this.error = this.hasError || false;
      } else {
        this.error = this.isRequired && this.hasError;
      }
    },
  },
  beforeUnmount() {
    clearTimeout(this.modelUpdateTimeout)
  }
});
</script>

<style scoped>
.input-wrapper {
  position: relative;
}

.input {
  display: flex;
  align-items: center;
  outline-width: 0;
  gap: 8px;
  width: 100%;
  background: #ffffff;
  color: #101828;
  border: 1px solid #d0d5dd;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  border-radius: 8px;
  overflow: hidden;
}
input:focus {
  outline: none;
}

.focused {
  background: #ffffff;
  border: 1px solid #fdc6c2;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05),
  0 0 0 4px rgba(139, 41, 35, 0.04);
  border-radius: 8px;
}

input:disabled {
  color: #667085;
  background: #f9fafb;
  cursor: default;
}
</style>
